import { useState } from 'react';
import type { HeliborneEntry } from '@/lib/types/division-profile';
import type { Unit } from '@/lib/types/unit';
import { useDataStore } from '@/lib/store/data-store';
import { getUnitImagePath } from '@/lib/utils/unit-images';
import { UnitInfoDialog } from '@/components/DeckBuilder/UnitInfoDialog';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface KpiHeliborneProps {
  label: string;
  value: number;
  entries: HeliborneEntry[];
}

export function KpiHeliborne({ label, value, entries }: KpiHeliborneProps) {
  const { getUnitByDescriptor } = useDataStore();
  const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);

  function openUnit(descriptorName: string) {
    const unit = getUnitByDescriptor(descriptorName);
    if (unit) {
      setSelectedUnit(unit);
      setDialogOpen(true);
    }
  }

  const hasEntries = entries.length > 0;
  const disabled = !hasEntries && value === 0;

  const pairLabel =
    entries.length === 1
      ? '1 squad–helo pair'
      : `${entries.length} squad–helo pairs`;

  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-lg border bg-muted/30 px-2 py-1.5 flex-1 min-w-[88px] gap-0.5',
        disabled && 'opacity-35'
      )}
    >
      <div className="flex flex-col items-center">
        <span className="text-sm font-bold tabular-nums leading-none text-foreground">
          {value}
        </span>
        <span className="text-[10px] text-muted-foreground mt-0.5 text-center leading-tight">
          {label}
        </span>
      </div>

      {hasEntries && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-[10px] text-center text-muted-foreground underline-offset-2 hover:text-foreground hover:underline leading-tight max-w-[10rem] px-0.5"
            >
              {pairLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[min(20rem,calc(100vw-2rem))] p-2.5"
            align="center"
            side="top"
            sideOffset={6}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-0.5">
              Heliborne combinations
            </p>
            <ul className="max-h-64 overflow-y-auto space-y-2 pr-0.5">
              {entries.map((e, i) => (
                <li
                  key={`${e.infantryDescriptor}-${i}`}
                  className="flex gap-2 border-b border-border/40 pb-2 text-xs last:border-0 last:pb-0"
                >
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => openUnit(e.infantryDescriptor)}
                      className="relative h-7 w-7 shrink-0 overflow-hidden rounded border bg-muted/50 hover:ring-1 hover:ring-primary"
                    >
                      <img
                        src={getUnitImagePath(e.infantryDescriptor)}
                        alt=""
                        className="size-full object-cover"
                        onError={(ev) => {
                          (ev.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      {e.cards > 1 ? (
                        <span className="absolute -bottom-0.5 -right-0.5 flex h-3 min-w-3 items-center justify-center rounded-full bg-foreground px-0.5 text-[8px] font-bold leading-none text-background">
                          {e.cards}
                        </span>
                      ) : null}
                    </button>
                    {e.helicopterDescriptors.map((heliDesc, hi) => (
                      <button
                        key={heliDesc}
                        type="button"
                        onClick={() => openUnit(heliDesc)}
                        className="relative h-6 w-6 shrink-0 overflow-hidden rounded border border-dashed border-muted-foreground/40 bg-muted/40 hover:ring-1 hover:ring-primary"
                        title={e.helicopterNames[hi]}
                      >
                        <img
                          src={getUnitImagePath(heliDesc)}
                          alt=""
                          className="size-full object-cover"
                          onError={(ev) => {
                            (ev.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium leading-snug">{e.infantryName}</p>
                    <p className="text-[10px] leading-snug text-muted-foreground">
                      via {e.helicopterNames.join(' · ')}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}

      {selectedUnit && (
        <UnitInfoDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          unit={selectedUnit}
        />
      )}
    </div>
  );
}
